'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
  DocumentSearchResult,
  useRenameDocument,
} from '@dc-shared-web/hooks/api/document'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@dc-shared-web/components/shadcn/form'
import { Input } from '@dc-shared-web/components/shadcn/input'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog } from '@dc-shared-web/components/base/BaseDialog'

const getFormSchema = (document: DocumentSearchResult) =>
  z.object({
    name: z
      .string()
      .min(1)
      .refine((value) => value !== document.fileName, {
        message: 'Document name must be different from current name.',
      }),
  })

export function RenameDocumentDialog({
  document,
  onClose,
  open,
}: {
  document: DocumentSearchResult
  onClose: () => void
  open: boolean
}) {
  const { mutate, isLoading: isRenamingDocument } = useRenameDocument({
    documentId: document.uuid,
    onSuccess: onClose,
  })
  const formSchema = getFormSchema(document)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: document.fileName,
    },
  })

  const onRenameSubmit = (data: z.infer<typeof formSchema>) => {
    mutate({
      uuid: document.uuid,
      label: data.name,
      oldName: document.fileName,
    })
  }

  return (
    <BaseDialog
      title='Rename'
      isOpen={open}
      onClose={onClose}
      primaryActionSlot={
        <Button
          disabled={isRenamingDocument || !form.formState.isValid}
          onClick={form.handleSubmit(onRenameSubmit)}
          data-testid='rename-document-dialog-save-button'
        >
          {isRenamingDocument ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      <Form {...form}>
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder='Enter crunch name' {...field} autoFocus />
              </FormControl>
            </FormItem>
          )}
        />
      </Form>
    </BaseDialog>
  )
}
